import { type FC, type ReactNode, useRef } from 'react';
import type { AmountInput as AmountInputComponent } from '@george-labs.com/design-system';
import { FormField, InputGroup, Paragraph, formatAmount } from '@george-labs.com/design-system';
import type {
    FORM_FIELD_LABEL_POSITION,
    FORM_FIELD_VARIANT,
} from '@george-labs.com/design-system/types/components/Form/FormField';

import { useField } from '@luna/form2';
import { getFeatureConfig } from '@luna/feature-config';
import { LocalizedAmountInput } from '@shared/core-ui-amount';

import { shouldShowError } from '../FieldErrorVisibilityService';

interface Props {
    'data-cy': string;
    amountFormControlName: string;
    precisionFormControlName: string;
    label?: ReactNode;
    placeholder?: string;
    labelPosition?: `${FORM_FIELD_LABEL_POSITION}`;
    variant?: `${FORM_FIELD_VARIANT}`;
    required?: boolean;
    disabled?: boolean;
    maxLength?: number;
    maxLengthAlert?: ReactNode;
    currencySelect?: ReactNode;
    description?: ReactNode;
    alert?: ReactNode;
    amountInput?: typeof AmountInputComponent | typeof LocalizedAmountInput;
    valueAsNumber?: boolean;
    valueAsUndefinedWhenNotSet?: boolean;
}

const DEFAULT_PLACEHOLDER_VALUE = '0';
const getDefaultPlaceholder = () =>
    formatAmount(DEFAULT_PLACEHOLDER_VALUE, {
        precision: getFeatureConfig((f) => f.base.defaultPrecision),
    });

const getMaxLength = (precision: number, maxInputLength?: number): number | undefined => {
    if (!maxInputLength) {
        return undefined;
    }
    return maxInputLength - precision;
};

export const AmountWithCurrencyFormField: FC<Props> = ({
    'data-cy': dataCy,
    amountFormControlName,
    precisionFormControlName,
    label,
    placeholder = getDefaultPlaceholder(),
    labelPosition = 'above',
    variant = 'default',
    required = false,
    disabled = false,
    maxLength,
    maxLengthAlert,
    currencySelect,
    description,
    alert,
    amountInput: AmountInput = LocalizedAmountInput,
    valueAsNumber,
    valueAsUndefinedWhenNotSet,
}) => {
    const [{ value: amount, onBlur }, { error, touched }, { setValue }] = useField<number | string>(
        amountFormControlName,
    );
    const [{ value: precision }] = useField<number>(precisionFormControlName);
    const amountInputRef = useRef<HTMLInputElement>(null);
    const isErrorMessageVisible = shouldShowError(true, touched, error);

    const handleInput = (amountObject: { value: number | string }) => {
        if (valueAsUndefinedWhenNotSet && !amountInputRef.current?.value) {
            setValue(undefined);
            return;
        }

        const value = valueAsNumber ? Number(amountObject.value) : amountObject.value;
        setValue(value);
    };

    return (
        <>
            <FormField
                data-cy={dataCy}
                label={label}
                labelPosition={labelPosition as FORM_FIELD_LABEL_POSITION}
                variant={variant as FORM_FIELD_VARIANT}
                error={
                    isErrorMessageVisible && (
                        <Paragraph data-cy={`${dataCy}-error`}>{error}</Paragraph>
                    )
                }
                required={required}
                disabled={disabled}
                input={
                    <InputGroup append={currencySelect}>
                        <AmountInput
                            innerRef={amountInputRef}
                            data-cy={`${dataCy}-value`}
                            name={amountFormControlName}
                            value={amount}
                            onInput={handleInput}
                            onBlur={onBlur}
                            placeholder={placeholder}
                            precision={precision}
                            maxLength={getMaxLength(precision, maxLength)}
                        />
                    </InputGroup>
                }
                description={description}
                alert={alert}
            />
            {maxLength && amount?.toString().length >= maxLength && maxLengthAlert}
        </>
    );
};
